import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EnemyType } from '@/components/game/enemies/EnemyType';

interface ScoreState {
  currentScore: number;
  highScore: number;
  enemiesKilled: Record<EnemyType, number>;
  totalEnemiesKilled: number;
  wavesCompleted: number;
  castleDamageReceived: number;
  resourcesCollected: number;
  resourcesSpent: number;
  trapsPlaced: number;
  gameTime: number;
}

const initialState: ScoreState = {
  currentScore: 0,
  highScore: 0,
  enemiesKilled: {
    [EnemyType.GRUNT]: 0,
    [EnemyType.ARCHER]: 0,
    [EnemyType.BRUTE]: 0,
    [EnemyType.BOSS]: 0
  },
  totalEnemiesKilled: 0,
  wavesCompleted: 0,
  castleDamageReceived: 0,
  resourcesCollected: 0,
  resourcesSpent: 0,
  trapsPlaced: 0,
  gameTime: 0
};

export const scoreSlice = createSlice({
  name: 'score',
  initialState,
  reducers: {
    addScore: (state, action: PayloadAction<number>) => {
      state.currentScore += action.payload;
      state.highScore = Math.max(state.highScore, state.currentScore);
    },
    
    addEnemyKill: (state, action: PayloadAction<{
      enemyType: EnemyType;
      scoreValue: number;
    }>) => {
      const { enemyType, scoreValue } = action.payload;
      state.enemiesKilled[enemyType]++;
      state.totalEnemiesKilled++;
      state.currentScore += scoreValue;
      state.highScore = Math.max(state.highScore, state.currentScore);
    },
    
    addWaveCompleted: (state, action: PayloadAction<number>) => {
      state.wavesCompleted++;
      state.currentScore += action.payload;
      state.highScore = Math.max(state.highScore, state.currentScore);
    },
    
    addCastleDamage: (state, action: PayloadAction<number>) => {
      state.castleDamageReceived += action.payload;
    },
    
    addResourcesCollected: (state, action: PayloadAction<number>) => {
      state.resourcesCollected += action.payload;
    },
    
    addResourcesSpent: (state, action: PayloadAction<number>) => {
      state.resourcesSpent += action.payload;
    },
    
    addTrapPlaced: (state) => {
      state.trapsPlaced++;
    },
    
    updateGameTime: (state, action: PayloadAction<number>) => {
      state.gameTime = action.payload;
    },
    
    resetCurrentScore: (state) => {
      state.currentScore = 0;
      state.enemiesKilled = {
        [EnemyType.GRUNT]: 0,
        [EnemyType.ARCHER]: 0,
        [EnemyType.BRUTE]: 0,
        [EnemyType.BOSS]: 0
      };
      state.totalEnemiesKilled = 0;
      state.wavesCompleted = 0;
      state.castleDamageReceived = 0;
      state.resourcesCollected = 0;
      state.resourcesSpent = 0;
      state.trapsPlaced = 0;
      state.gameTime = 0;
    },
    
    resetAllScores: (state) => {
      return initialState;
    }
  }
});

export const {
  addScore,
  addEnemyKill,
  addWaveCompleted,
  addCastleDamage,
  addResourcesCollected,
  addResourcesSpent,
  addTrapPlaced,
  updateGameTime,
  resetCurrentScore,
  resetAllScores
} = scoreSlice.actions;

export default scoreSlice.reducer; 